import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { environment } from '@environments/environment';
import { Contract } from '@interfaces/rest/interfaces/contract';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RestService } from '@services/rest.service';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { ContractSignModalComponent } from '../contract-sign-modal/contract-sign-modal.component';

export interface ContractsModalConfig {
  title?: string;
  provinceId: number;
  contracts?: Contract[];
}

@Component({
  selector: 'app-contracts-modal',
  templateUrl: './contracts-modal.component.html',
  styleUrls: ['./contracts-modal.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule]
})
export class ContractsModalComponent implements OnInit {

  @Input() config!: ContractsModalConfig;
  contracts: Contract[] = [];
  signingContractIds: Set<number> = new Set();
  sendingEmailContractIds: Set<number> = new Set();

  constructor(
    private rest: RestService,
    private modalController: ModalController,
    private utils: DpUtilsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    // Если контракты переданы в конфиге, используем их
    if (this.config?.contracts && this.config.contracts.length > 0) {
      this.contracts = this.config.contracts;
    } else if (this.config?.provinceId) {
      await this.loadProvinceContracts();
    }
  }

  /**
   * Загрузка контрактов провинции, ожидающих подписания
   */
  async loadProvinceContracts() {
    if (!this.config?.provinceId) {
      return;
    }

    await this.utils.showLoading();
    try {
      const result = await this.rest.getProvincePendingContracts(this.config.provinceId);
      if (result.ok && result.response) {
        this.contracts = result.response;
      }
    } catch (error) {
      console.error('Error loading province contracts:', error);
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.load_error'),
        cssClass: 'danger'
      });
    } finally {
      await this.utils.hideLoading();
    }
  }

  async close() {
    // Закрываем модальное окно вручную
    // Если все контракты подписаны, передаем флаг allSigned: true
    if (this.contracts.length === 0) {
      this.modalController.dismiss({ allSigned: true });
    } else {
      // Если есть неподписанные контракты, показываем предупреждение
      // Модальное окно не закроется из-за canDismiss: false
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.cannot_close_with_unsigned'),
        cssClass: 'warning',
        duration: 3000
      });
    }
  }

  /**
   * Подписание контракта
   */
  async signContract(contract: Contract) {
    if (this.signingContractIds.has(contract.id)) {
      return;
    }

    // Открываем модальное окно для подписания
    const modal = await this.modalController.create({
      component: ContractSignModalComponent,
      componentProps: {
        config: {
          title: this.translate.instant('contracts.sign_contract'),
          contract: contract,
          provinceId: this.config.provinceId
        }
      },
      cssClass: 'contract_sign_modal'
    });

    await modal.present();
    const signResult = await modal.onWillDismiss();

    if (!signResult || !signResult.data?.signed) {
      return;
    }

    this.signingContractIds.add(contract.id);
    await this.utils.showLoading();

    try {
      const result = await this.rest.signProvinceContract(
        contract.id,
        signResult.data.signature,
        this.config.provinceId
      );

      if (!result.ok) {
        const errorMessage = (result.response as any)?.message || this.translate.instant('province_contracts.sign_error');
        await this.utils.showToast({
          message: errorMessage,
          cssClass: 'danger'
        });
        return;
      }

      const signResponse = result.response;

      await this.utils.showToast({
        message: this.translate.instant('province_contracts.contract_signed'),
        cssClass: 'success'
      });

      // Удаляем контракт из списка после успешного подписания
      this.contracts = this.contracts.filter(c => c.id !== contract.id);

      // Если все контракты подписаны, показываем пустое состояние с сообщением об успехе
      // и закрываем модальное окно через 2 секунды после отображения сообщения
      if (signResponse.allContractsSigned || this.contracts.length === 0) {
        // Принудительно обновляем представление, чтобы показать пустое состояние
        this.cdr.detectChanges();

        // Закрываем модальное окно через 2 секунды после отображения сообщения об успехе
        setTimeout(() => {
          this.modalController.dismiss({ allSigned: true });
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error signing contract:', error);
      const errorMessage = error?.error?.message || error?.message || this.translate.instant('province_contracts.sign_error');
      await this.utils.showToast({
        message: errorMessage,
        cssClass: 'danger'
      });
    } finally {
      this.signingContractIds.delete(contract.id);
      await this.utils.hideLoading();
    }
  }

  /**
   * Получение читаемого названия типа контракта
   */
  getContractTypeLabel(type: string | null): string {
    if (!type) return this.translate.instant('province_contracts.no_type');

    const typeLabels: { [key: string]: string } = {
      'driver_agreement': this.translate.instant('contracts.types.driver_agreement'),
      'service_contract': this.translate.instant('contracts.types.service_contract'),
      'privacy_policy': this.translate.instant('contracts.types.privacy_policy')
    };

    return typeLabels[type] || type;
  }

  /**
   * Форматирование даты для отображения
   */
  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Открытие PDF контракта в браузере
   */
  async openPdf(pdfUrl: string | undefined) {
    if (!pdfUrl) {
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.pdf_not_available'),
        cssClass: 'warning'
      });
      return;
    }

    try {
      const fullUrl = pdfUrl.startsWith('http')
        ? pdfUrl
        : `${environment.apiMainUrl}/${pdfUrl}`;

      await Browser.open({ url: fullUrl });
    } catch (error) {
      console.error('Error opening PDF:', error);
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.pdf_open_error'),
        cssClass: 'danger'
      });
    }
  }
}
